import { FC, useCallback, useEffect } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';
import type mapboxGl from 'mapbox-gl';

/**
 * Area-config geometry for the Type Curve Scenarios listing view.
 *
 * The map draws every scenario area matching the current filters; the client
 * sends the filter payload on the tile url and the tile resolves and renders the
 * full matching set (`Map::Tiles::TypeCurveScenarioAreaConfigs`, features tagged
 * with `type_curve_scenario_id`). Clicking a listing row highlights that
 * scenario; clicking an area on the map reports its scenario id so the page
 * selects the owning row.
 */

const SOURCE_ID = 'tc-listing-scenario-areas';
const SOURCE_LAYER = 'type-curve-scenario-areas';
const FILL_LAYER_ID = `${SOURCE_ID}-fill`;

/** Type Curve Scenario areas — same red as the listing's Type Curve areas. */
const AREA_FILL = '#da1e28';
const AREA_LINE = '#da1e28';

/** Selected area: filled so it reads at a glance, not just an outline. */
const SELECTED_COLOR = '#f1c21b';
const SELECTED_BORDER = '#b28600';

/** Matches no feature, ever — two unequal literals. */
const MATCH_NOTHING: mapboxGl.Expression = [
  '==',
  ['literal', 0],
  ['literal', 1],
];

/** Tiles emit a `shape` polygon and a `centroid` point per area geometry. */
const SHAPE_ONLY: mapboxGl.Expression = ['==', ['get', 'type'], 'shape'];
const CENTROID_ONLY: mapboxGl.Expression = ['==', ['get', 'type'], 'centroid'];

interface TcListingScenarioAreasLayerProps {
  /** Vector tile template(s) from `useTcTileUrls` — carry the listing filters. */
  tileUrls: string[] | null;
  /** Selected scenario id, highlighted via the feature's `type_curve_scenario_id`. */
  selectedScenarioId?: number | null;
  /** Reports a clicked area's `type_curve_scenario_id` so the page selects its row. */
  onSelectScenarioId?: (scenarioId: number) => void;
}

export const TcListingScenarioAreasLayer: FC<
  TcListingScenarioAreasLayerProps
> = ({ tileUrls, selectedScenarioId = null, onSelectScenarioId }) => {
  const { current: mapRef } = useMap();

  const onClick = useCallback(
    (event: mapboxGl.MapLayerMouseEvent) => {
      const map = mapRef?.getMap();
      if (!map || !onSelectScenarioId) return;
      const feature = map.queryRenderedFeatures(event.point, {
        layers: [FILL_LAYER_ID],
      })[0];
      const id = feature?.properties?.type_curve_scenario_id;
      if (id === undefined || id === null) return;
      onSelectScenarioId(Number(id));
    },
    [mapRef, onSelectScenarioId]
  );

  useEffect(() => {
    const map = mapRef?.getMap();
    if (!map) return undefined;
    map.on('click', FILL_LAYER_ID, onClick);
    return () => {
      map.off('click', FILL_LAYER_ID, onClick);
    };
  }, [mapRef, onClick]);

  if (!tileUrls) return null;

  const highlightFilter: mapboxGl.Expression =
    selectedScenarioId === null
      ? MATCH_NOTHING
      : [
          'all',
          SHAPE_ONLY,
          ['==', ['get', 'type_curve_scenario_id'], selectedScenarioId],
        ];

  // The base fill skips the selected shapes: two semi-transparent fills stacked
  // composite (red under yellow reads orange), so the highlight only looks like
  // its own colour when nothing is painted beneath it.
  const baseFillFilter: mapboxGl.Expression =
    selectedScenarioId === null
      ? SHAPE_ONLY
      : [
          'all',
          SHAPE_ONLY,
          ['!=', ['get', 'type_curve_scenario_id'], selectedScenarioId],
        ];

  return (
    // `tiles` is the only mutating prop — see the note in tc-listing-areas-layer.tsx.
    <Source id={SOURCE_ID} type="vector" tiles={tileUrls}>
      <Layer
        id={FILL_LAYER_ID}
        type="fill"
        source-layer={SOURCE_LAYER}
        filter={baseFillFilter}
        paint={{ 'fill-color': AREA_FILL, 'fill-opacity': 0.3 }}
      />
      <Layer
        id={`${SOURCE_ID}-line`}
        type="line"
        source-layer={SOURCE_LAYER}
        filter={SHAPE_ONLY}
        paint={{
          'line-color': AREA_LINE,
          'line-width': 2,
          'line-opacity': 0.9,
        }}
      />
      <Layer
        id={`${SOURCE_ID}-selected-fill`}
        type="fill"
        source-layer={SOURCE_LAYER}
        filter={highlightFilter}
        paint={{ 'fill-color': SELECTED_COLOR, 'fill-opacity': 0.55 }}
      />
      <Layer
        id={`${SOURCE_ID}-selected-line`}
        type="line"
        source-layer={SOURCE_LAYER}
        filter={highlightFilter}
        paint={{ 'line-color': SELECTED_BORDER, 'line-width': 2 }}
      />
      <Layer
        id={`${SOURCE_ID}-label`}
        type="symbol"
        source-layer={SOURCE_LAYER}
        filter={CENTROID_ONLY}
        minzoom={6}
        layout={{ 'text-field': ['get', 'name'], 'text-size': 11 }}
        paint={{
          'text-color': AREA_LINE,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1,
        }}
      />
    </Source>
  );
};
